<i18n locale="en">
{
  "ariaLabel": "Image gallery grid"
}
</i18n>

<template>
  <div
    ref="gridRef"
    role="grid"
    tabindex="0"
    class="image-gallery-grid"
    :aria-label="t('ariaLabel')"
    @keydown="handleKeyDown"
  >
    <ImageGalleryItem
      v-for="(item, index) in items"
      :key="item.id"
      :item="item"
      :is-focused="index === focusedIndex"
      @click="handleItemClick(item)"
      @retry="$emit('item-retry', $event)"
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ImageGalleryItem from './ImageGalleryItem.vue'
import type { GalleryItem } from './types'

interface Props {
  items: GalleryItem[]
}

interface Emits {
  (event: 'item-click', item: GalleryItem): void
  (event: 'placeholder-click'): void
  (event: 'item-retry', item: GalleryItem): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()
const gridRef = ref<HTMLElement | null>(null)
const focusedIndex = ref(0)

onMounted(() => {
  if (gridRef.value) {
    gridRef.value.focus()
  }
})

watch(
  () => props.items.length,
  () => {
    if (focusedIndex.value >= props.items.length) {
      focusedIndex.value = Math.max(0, props.items.length - 1)
    }
  }
)

/**
 * Handles keyboard navigation within the gallery grid
 * @param {KeyboardEvent} event - The keyboard event
 */
function handleKeyDown(event: KeyboardEvent): void {
  const { key } = event
  const gridColumns = 2

  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) {
    event.preventDefault()

    if (key === 'ArrowLeft') {
      focusedIndex.value = Math.max(0, focusedIndex.value - 1)
    } else if (key === 'ArrowRight') {
      focusedIndex.value = Math.min(
        props.items.length - 1,
        focusedIndex.value + 1
      )
    } else if (key === 'ArrowUp') {
      focusedIndex.value = Math.max(0, focusedIndex.value - gridColumns)
    } else if (key === 'ArrowDown') {
      focusedIndex.value = Math.min(
        props.items.length - 1,
        focusedIndex.value + gridColumns
      )
    }
  } else if (key === 'Enter') {
    event.preventDefault()
    const item = props.items[focusedIndex.value]
    if (item) {
      handleItemClick(item)
    }
  }
}

/**
 * Handles click on a gallery item
 * @param {GalleryItem} item - The clicked gallery item
 */
function handleItemClick(item: GalleryItem): void {
  if (item.state === 'placeholder') {
    emit('placeholder-click')
  } else {
    emit('item-click', item)
  }
}
</script>

<style lang="scss" scoped>
.image-gallery-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  outline: none;

  &:focus {
    outline: none;
  }
}
</style>
